import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Accordion, AccordionDetails, AccordionSummary, FormGroup } from "@mui/material";
import { ButtonArrow } from "@thrivetribe/mvf.assets.icons.button-arrow";
import { Button } from "@thrivetribe/mvf.ui.atoms.button";
import { FormikTextArea } from "@thrivetribe/mvf.ui.atoms.formik-text-area";
import { LoadingSpinner } from "@thrivetribe/mvf.ui.molecules.loading-spinner";
import {
  OnboardingPage,
  OnboardingPageBody,
  OnboardingPageContent,
  OnboardingPageCopy,
  OnboardingPageFooter,
} from "@thrivetribe/mvf.ui.organisms.onboarding-page";
import { Form as FormikForm, Formik } from "formik";
import { signupNavLink } from "Onboarding/utils";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { AllergiesValidationSchema } from "../OnboardingValidations";
import "./Allergies.scss";
import { useGetUserQuery, useUpdateUserMutation } from "redux/userApiSlice";
import { useUser } from "components/user/UserContext";
import { Gtag } from "configs/ReactGa";
import { ReactGa } from "ui/atoms/ReactGa";
import { ImagesUrl } from "configs/EnvironmentConfig";

type FormValueTypes = {
  allergies: string;
};

const SpecifyAllergies = () => {
  let navigate = useNavigate();
  let user = useUser();
  let { data: userObj, isLoading } = useGetUserQuery(user?.userId);
  let [updateProfile] = useUpdateUserMutation({
    fixedCacheKey: user?.userId,
  });

  const updateProfileData = async (allergies: string) => {
    if (userObj) {
      const updatedProfile = { ...userObj, healthConditionDetails: { ...userObj.healthConditionDetails, allergies } };
      await updateProfile(updatedProfile)
        .unwrap()
        .then(() => {
          Gtag("event", "specify_allergies_set", {
            user_id: user.userId,
            event_category: "account setup",
          });
          navigate("/portal-setup/any-medication");
        });
    }
  };

  const initialValues: FormValueTypes = {
    allergies: userObj?.healthConditionDetails?.allergies || "",
  };

  const handleSubmit = async (values: FormValueTypes) => {
    updateProfileData(values.allergies);
  };

  const handleBack = async (values: FormValueTypes) => {
    updateProfileData(values.allergies).then(() => {
      navigate("/portal-setup/any-allergies");
    });
  };

  if (isLoading) {
    return <LoadingSpinner fullscreen={true} />;
  }

  return (
    <div className="specify-allergies">
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={AllergiesValidationSchema}
        validateOnChange={true}
      >
        {({ values }) => {
          return (
            <FormikForm className="onboarding-page__form">
              <Helmet>
                {user.isLegacy ? <title>Set up - Specify allergies</title> : <title>Sign up - Specify allergies</title>}
              </Helmet>
              <OnboardingPage
                user={user.displayName}
                links={signupNavLink}
                image={`${ImagesUrl}/football.webp`}
                title={user.isLegacy ? "Set up" : "Sign up"}
                onBackClick={() => handleBack(values)}
              >
                <OnboardingPageContent hasStickyFooter centralizeContent>
                  <OnboardingPageBody>
                    <OnboardingPageCopy>
                      <h1>What are your allergies?</h1>
                    </OnboardingPageCopy>
                    <FormGroup className="onboarding-page__content--group">
                      <FormikTextArea id="allergies" name="allergies" label="Fill us in on any allergies" />
                    </FormGroup>
                    <Accordion square={true} elevation={0} className="onboarding-page__accordion">
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon fontSize="large" />}
                        aria-controls={`question-explanation`}
                      >
                        <span>Hey, that’s private! Here’s why we ask.</span>
                      </AccordionSummary>
                      <AccordionDetails>
                        <span>
                          The more we know about your health, the better we can support you. Your data is safe with us.
                          For full details check out our{" "}
                          <a href="https://manvfat.com/privacy-policy/" rel="noreferrer" target="_blank">
                            Privacy policy
                          </a>
                          .
                        </span>
                      </AccordionDetails>
                    </Accordion>
                  </OnboardingPageBody>
                  <OnboardingPageFooter>
                    <Button type="submit" variant="primary" color="primary">
                      Continue
                      <ButtonArrow />
                    </Button>
                  </OnboardingPageFooter>
                </OnboardingPageContent>
              </OnboardingPage>
            </FormikForm>
          );
        }}
      </Formik>
      <ReactGa
        eventName="specify_allergies_requested"
        eventParams={{ user_id: user.userId, event_category: "account setup" }}
      />
    </div>
  );
};

export default SpecifyAllergies;
